import React from 'react';
import { ShieldCheck, Truck, MapPin, ArrowDown } from 'lucide-react';

export default function WhyPricingAndDelivery() {
  return (
    <section id="pricing-and-delivery" className="bg-[#EDEDEA] py-20 text-[#141414] border-t border-b border-[#141414]/10">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">

        {/* SECTION 13.5 — WHY $999 CAD */}
        <div id="why-999-cad" className="space-y-6">
          <div>
            <span className="text-xs md:text-sm font-mono font-bold text-ows-teal uppercase tracking-[0.6em] mb-4 block">
              WHY $999 CAD
            </span>
            <h2 className="text-3xl md:text-5xl font-display font-black text-[#141414] uppercase tracking-tight leading-tight mb-6">
              FACTORY DIRECT. NO MIDDLEMAN. NO MARKUP.
            </h2>
            <p className="text-base md:text-lg text-[#141414]/85 font-sans leading-relaxed">
              The OWS Expedition CX2 is built to order. Your deposit triggers your unit's production run — no warehouse inventory, no distributor margin, no retail markup. You pay what it actually costs to build a board at this specification. That is why the Batch 01 price is $999 CAD. After 200 units, standard retail is $1,299 CAD.
            </p>
          </div>

          {/* Warranty Card (Teal Left Border) */}
          <div className="bg-white border-l-4 border-ows-teal p-6 md:p-8 rounded-r-2xl shadow-sm space-y-5">
            <div className="flex items-center gap-2.5 text-ows-teal">
              <ShieldCheck className="h-5 w-5" />
              <span className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-[#141414]">
                MANUFACTURER'S WARRANTY
              </span>
            </div>

            <div className="space-y-3">
              <h3 className="text-base md:text-lg font-bold text-[#141414] uppercase tracking-wide">
                3-Year Craft Warranty
              </h3>

              <p className="text-sm md:text-base text-[#141414]/85 font-sans leading-relaxed">
                Every OWS Expedition CX2 is covered by a 3-year warranty administered
                directly by Open Water Syndicate. If your board has a structural or
                material defect under normal expedition use, we stand behind it.
              </p>
            </div>

            <div className="space-y-2.5">
              <h4 className="text-sm font-bold uppercase tracking-wider text-[#141414]">
                What’s Covered
              </h4>

              <ul className="list-disc pl-5 space-y-1.5 text-sm text-[#141414]/85 leading-relaxed">
                <li>Manufacturing defects and seam integrity.</li>
                <li>Air chamber failure under normal inflation pressure.</li>
                <li>Material separation under standard expedition loads.</li>
              </ul>
            </div>

            <div className="space-y-2.5">
              <h4 className="text-sm font-bold uppercase tracking-wider text-[#141414]">
                What’s Not Covered
              </h4>

              <ul className="list-disc pl-5 space-y-1.5 text-sm text-[#141414]/85 leading-relaxed">
                <li>
                  Physical impact damage, cuts, punctures, or abrasion from improper use
                  or storage.
                </li>
                <li>Damage resulting from exceeding 20 PSI maximum inflation.</li>
                <li>
                  Failure caused by improper inflation sequence or lack of fresh-water
                  maintenance after saltwater exposure.
                </li>
              </ul>
            </div>

            <div className="bg-[#f5f5f5] border border-[#141414]/10 rounded-xl p-4 md:p-5 space-y-3">
              <h4 className="text-sm font-bold uppercase tracking-wider text-[#141414]">
                ⚠️ Critical Inflation Procedure
              </h4>

              <div className="space-y-2 text-sm text-[#141414]/85 leading-relaxed">
                <p>
                  <span className="font-bold text-[#141414]">Step 1:</span> Always
                  inflate the outer main chamber first to primary pressure.
                </p>
                <p>
                  <span className="font-bold text-[#141414]">Step 2:</span> Inflate the
                  inner secondary chamber second.
                </p>
                <p>
                  Failing to follow this order can place unnecessary stress on the
                  internal bulkhead seam.
                </p>
              </div>
            </div>

            <div className="space-y-2.5">
              <h4 className="text-sm font-bold uppercase tracking-wider text-[#141414]">
                Care &amp; Maintenance
              </h4>

              <ul className="list-disc pl-5 space-y-1.5 text-sm text-[#141414]/85 leading-relaxed">
                <li>Rinse with fresh water after every saltwater session.</li>
                <li>Store deflated, completely dry, and out of direct sunlight.</li>
                <li>
                  A board that is maintained and inflated properly will outlast its
                  warranty by years.
                </li>
              </ul>
            </div>

            <div className="pt-4 border-t border-[#141414]/10">
              <p className="text-xs md:text-sm font-mono font-bold uppercase tracking-wider text-ows-teal">
                Warranty claims handled directly by Open Water Syndicate — no third
                party.
              </p>
            </div>
          </div>
        </div>

        {/* SEPARATOR */}
        <hr className="border-t border-[#141414]/15" />

        {/* SECTION 13.6 — DELIVERY */}
        <div id="delivery" className="space-y-6">
          <div>
            <span className="text-xs md:text-sm font-mono font-bold text-ows-teal uppercase tracking-[0.6em] mb-4 block">
              DELIVERY
            </span>
            <h2 className="text-3xl md:text-5xl font-display font-black text-[#141414] uppercase tracking-tight leading-tight mb-6">
              BUILT IN BATCH. SHIPPED WHEN READY.
            </h2>
            <p className="text-base md:text-lg text-[#141414]/85 font-sans leading-relaxed">
              Once your unit is complete and arrives at our Toronto, Ontario facility, we collect your remaining 70% balance and ship immediately.
            </p>
          </div>

          {/* Two Side-by-Side Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Card 1: Canada-Wide Delivery */}
            <div className="bg-white p-6 md:p-8 rounded-2xl border border-[#141414]/10 shadow-sm space-y-2.5">
              <div className="flex items-center gap-2.5 text-ows-teal">
                <Truck className="h-5 w-5" />
                <span className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-[#141414]">
                  CANADA-WIDE DELIVERY
                </span>
              </div>
              <p className="text-base md:text-lg font-bold text-[#141414] font-sans">
                $100 CAD flat rate. Anywhere in Canada.
              </p>
            </div>

            {/* Card 2: Toronto Pickup */}
            <div className="bg-white p-6 md:p-8 rounded-2xl border border-[#141414]/10 shadow-sm space-y-2.5">
              <div className="flex items-center gap-2.5 text-ows-teal">
                <MapPin className="h-5 w-5" />
                <span className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-[#141414]">
                  TORONTO PICKUP
                </span>
              </div>
              <p className="text-base md:text-lg font-bold text-[#141414] font-sans">
                Free. Collect directly in Toronto, Ontario.
              </p>
            </div>
          </div>

          {/* Fine Print */}
          <p className="text-xs font-mono text-[#141414]/60 leading-relaxed pt-1">
            International shipping available. Rates calculated at time of balance collection based on destination and carrier rates.
          </p>

          {/* CTA Link to Configurator */}
          {/* <div className="pt-4 text-center sm:text-left">
            <a
              href="#configurator"
              onClick={(e) => { e.preventDefault(); document.getElementById('configurator')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="inline-flex items-center justify-center gap-3 px-6 sm:px-10 py-4 sm:py-5 bg-ows-teal text-white text-[10px] font-mono font-bold tracking-[0.2em] sm:tracking-[0.3em] uppercase hover:bg-[#141414] transition-all rounded-full shadow-2xl group w-full sm:w-auto"
            >
              PRE-ORDER CX2 <ArrowDown className="h-4 w-4 group-hover:translate-y-1 transition-transform shrink-0" />
            </a>
          </div> */}
        </div>

      </div>
    </section>
  );
}
